/**
 * Moderne Winkelwagen Module
 * Een geoptimaliseerde en betrouwbare winkelwagenfunctie voor Shopify-webshops
 */

class ModerneWinkelwagen {
  constructor() {
    // DOM-elementen die we nodig hebben
    this.cartButtons = document.querySelectorAll('.add-to-cart-btn, [data-add-to-cart], .add-to-cart, .product-form__submit, button[name="add"], .ajax-cart-btn');
    this.cartCounters = document.querySelectorAll('.cart-count, .cart-counter, .icon-cart-count');
    this.cartDrawerTriggers = document.querySelectorAll('.cart-icon, .cart-link, [data-cart-drawer-trigger]');
    
    // Configuratie-opties
    this.config = {
      animatieDuur: 800,
      notificatieDuur: 3000,
      useCartDrawer: true,
    };
    
    // Status
    this.isUpdating = false;
    
    // Initialiseren
    this.init();
  }
  
  /**
   * Initialiseer alle functionaliteit
   */
  init() {
    this.createCartNotification();
    this.bindEvents();
    this.updateCartDisplay();
    this.initProcessExistingHandlers();
    this.setupMutationObserver();
    
    // Aangepast event voor externe integratie
    document.dispatchEvent(new CustomEvent('winkelwagen:geladen'));
    
    console.log('✅ ModerneWinkelwagen geïnitialiseerd');
  }
  
  /**
   * Maak notificatie-element aan als deze nog niet bestaat
   */
  createCartNotification() {
    if (!document.getElementById('winkelwagen-notificatie')) {
      const notificatie = document.createElement('div');
      notificatie.id = 'winkelwagen-notificatie';
      notificatie.className = 'winkelwagen-notificatie';
      notificatie.innerHTML = `
        <div class="notificatie-inhoud">
          <div class="notificatie-icoon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"></path>
              <path d="M20 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"></path>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </div>
          <div class="notificatie-bericht">Product toegevoegd aan winkelwagen</div>
        </div>
      `;
      document.body.appendChild(notificatie);
      
      // Voeg direct CSS voor notificatie toe als inline style (tenzij al aanwezig in CSS)
      if (!document.getElementById('winkelwagen-notificatie-style')) {
        const style = document.createElement('style');
        style.id = 'winkelwagen-notificatie-style';
        style.innerHTML = `
          .winkelwagen-notificatie {
            position: fixed;
            bottom: 20px;
            right: 20px;
            background-color: #fff;
            color: #333;
            border-radius: 8px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
            z-index: 9999;
            transform: translateY(100px);
            opacity: 0;
            transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            pointer-events: none;
            padding: 16px;
            max-width: 300px;
          }
          
          .winkelwagen-notificatie.toon {
            transform: translateY(0);
            opacity: 1;
          }
          
          .notificatie-inhoud {
            display: flex;
            align-items: center;
          }
          
          .notificatie-icoon {
            margin-right: 12px;
            color: #4CAF50;
          }
          
          .notificatie-bericht {
            font-weight: 500;
          }
          
          /* Animatie voor toevoegen aan winkelwagen */
          @keyframes winkelwagen-pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          
          .winkelwagen-pulse {
            animation: winkelwagen-pulse 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          
          .add-to-cart-btn.toevoegen {
            position: relative;
            overflow: hidden;
          }
          
          .add-to-cart-btn.toevoegen::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0%;
            height: 0%;
            background-color: rgba(255, 255, 255, 0.25);
            border-radius: 50%;
            transform: translate(-50%, -50%);
            z-index: 1;
            animation: ripple 0.6s ease-out;
          }
          
          @keyframes ripple {
            0% {
              width: 0%;
              height: 0%;
              opacity: 0.6;
            }
            100% {
              width: 200%;
              height: 200%;
              opacity: 0;
            }
          }
          
          /* Responsieve aanpassingen */
          @media (max-width: 767px) {
            .winkelwagen-notificatie {
              bottom: 10px;
              right: 10px;
              left: 10px;
              max-width: calc(100% - 20px);
            }
          }
        `;
        document.head.appendChild(style);
      }
    }
    
    return document.getElementById('winkelwagen-notificatie');
  }
  
  /**
   * Koppel event listeners
   */
  bindEvents() {
    // "Toevoegen aan winkelwagen" knoppen
    this.cartButtons.forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
    
    // Winkelwagen drawer/popup openen
    this.cartDrawerTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleCartTriggerClick.bind(this));
    });
    
    // Luister naar wijzigingen in de winkelwagen (aangeraden door Shopify)
    document.addEventListener('cart:refresh', this.updateCartDisplay.bind(this));
    document.addEventListener('cart:update', this.updateCartDisplay.bind(this));
    document.addEventListener('product:added', this.updateCartDisplay.bind(this));
    
    // Bij laden pagina winkelwagen controleren
    window.addEventListener('load', this.updateCartDisplay.bind(this));
  }
  
  /**
   * Beheer toevoegen aan winkelwagen
   */
  async handleAddToCart(event) {
    event.preventDefault();
    
    // Als al bezig met update, voorkom dubbel klikken
    if (this.isUpdating) return;
    
    const button = event.currentTarget;
    
    // Als de knop in een form zit, gebruik dan het form
    const form = button.closest('form[action*="/cart/add"]');
    if (form && (button.type === 'submit' || button.name === 'add')) {
      // Voorkom dubbele inzending
      this.isUpdating = true;
      
      // UI feedback
      const originalText = button.innerHTML;
      button.classList.add('toevoegen');
      button.disabled = true;
      
      try {
        // Verzamel formuliergegevens
        const formData = new FormData(form);
        
        // Voeg product toe via AJAX
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        const data = await response.json();
        
        // Controleer op fouten
        if (data.status && data.description) {
          throw new Error(data.description);
        }
        
        // Winkelwagen bijwerken
        this.updateCartDisplay();
        
        // Toon notificatie
        this.toonNotificatie('Product toegevoegd aan winkelwagen');
        
        // Animatie voor winkelwagen icoon
        this.animateCartIcon();
        
        // Open winkelwagen drawer indien ingeschakeld
        if (this.config.useCartDrawer) {
          this.openCartDrawer();
        }
        
        // Reset knop met succesanimatie
        setTimeout(() => {
          button.classList.remove('toevoegen');
          button.classList.add('toegevoegd');
          
          // Terug naar originele staat na animatie
          setTimeout(() => {
            button.classList.remove('toegevoegd');
            button.disabled = false;
            button.innerHTML = originalText;
            this.isUpdating = false;
          }, 1500);
        }, 600);
        
      } catch (error) {
        console.error('Fout bij toevoegen aan winkelwagen:', error);
        
        // Toon foutmelding aan gebruiker
        this.toonNotificatie(`Fout: ${error.message || 'Kon product niet toevoegen'}`, 'error');
        
        // Reset knop
        button.classList.remove('toevoegen');
        button.disabled = false;
        button.innerHTML = originalText;
        this.isUpdating = false;
      }
      
      return;
    }
    
    // Standaard verwerking voor niet-formulier knoppen
    const variantId = button.dataset.variantId || button.getAttribute('data-variant-id');
    const quantity = parseInt(button.dataset.quantity || 1);
    
    if (!variantId) {
      console.error('Geen variant ID gevonden voor product');
      return;
    }
    
    // Visuele feedback dat we bezig zijn
    this.isUpdating = true;
    
    // Opslaan van originele knoptekst
    const originalText = button.innerHTML;
    button.classList.add('toevoegen');
    button.disabled = true;
    
    if (button.querySelector('.knop-tekst')) {
      button.querySelector('.knop-tekst').textContent = 'Toevoegen...';
    } else {
      button.innerHTML = 'Toevoegen...';
    }
    
    try {
      // Verzenden van verzoek aan Shopify API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: quantity
          }]
        })
      });
      
      const data = await response.json();
      
      // Controleer op fouten
      if (data.status && data.description) {
        throw new Error(data.description);
      }
      
      // Winkelwagen bijwerken
      this.updateCartDisplay();
      
      // Toon notificatie
      this.toonNotificatie('Product toegevoegd aan winkelwagen');
      
      // Animatie voor winkelwagen icoon
      this.animateCartIcon();
      
      // Open winkelwagen drawer indien ingeschakeld
      if (this.config.useCartDrawer) {
        this.openCartDrawer();
      }
      
      // Reset knop met succesanimatie
      setTimeout(() => {
        button.classList.remove('toevoegen');
        button.classList.add('toegevoegd');
        
        if (button.querySelector('.knop-tekst')) {
          button.querySelector('.knop-tekst').textContent = 'Toegevoegd ✓';
        } else {
          button.innerHTML = 'Toegevoegd ✓';
        }
        
        // Terug naar originele staat na animatie
        setTimeout(() => {
          button.classList.remove('toegevoegd');
          button.disabled = false;
          button.innerHTML = originalText;
          this.isUpdating = false;
        }, 1500);
      }, 600);
      
    } catch (error) {
      console.error('Fout bij toevoegen aan winkelwagen:', error);
      
      // Toon foutmelding aan gebruiker
      this.toonNotificatie(`Fout: ${error.message || 'Kon product niet toevoegen'}`, 'error');
      
      // Reset knop
      button.classList.remove('toevoegen');
      button.disabled = false;
      button.innerHTML = originalText;
      this.isUpdating = false;
    }
  }
  
  /**
   * Toon een notificatie
   */
  toonNotificatie(bericht, type = 'success') {
    const notificatie = document.getElementById('winkelwagen-notificatie');
    const berichtElement = notificatie.querySelector('.notificatie-bericht');
    const icoonElement = notificatie.querySelector('.notificatie-icoon');
    
    // Zet juiste bericht en stijl
    berichtElement.textContent = bericht;
    
    if (type === 'error') {
      icoonElement.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      `;
      icoonElement.style.color = '#F44336';
    } else {
      icoonElement.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"></path>
          <path d="M20 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"></path>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg>
      `;
      icoonElement.style.color = '#4CAF50';
    }
    
    // Toon notificatie
    notificatie.classList.add('toon');
    
    // Verberg na bepaalde tijd
    if (notificatie.hideTimeout) {
      clearTimeout(notificatie.hideTimeout);
    }
    
    notificatie.hideTimeout = setTimeout(() => {
      notificatie.classList.remove('toon');
    }, this.config.notificatieDuur);
  }
  
  /**
   * Animeer het winkelwagenicoontje
   */
  animateCartIcon() {
    this.cartCounters.forEach(counter => {
      const cartIcon = counter.closest('.cart-icon, .cart-link');
      if (cartIcon) {
        cartIcon.classList.add('winkelwagen-pulse');
        setTimeout(() => {
          cartIcon.classList.remove('winkelwagen-pulse');
        }, 700);
      }
    });
  }
  
  /**
   * Update de weergave van de winkelwagen (aantal items)
   */
  async updateCartDisplay() {
    try {
      // Haal winkelwagen op via Shopify AJAX API
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      const cart = await response.json();
      
      // Update alle tellers
      this.cartCounters.forEach(counter => {
        // Correct weergeven van aantal items
        counter.textContent = cart.item_count;
        
        // Juiste weergave voor 0 items
        if (cart.item_count === 0) {
          counter.style.display = 'none';
        } else {
          counter.style.display = 'flex';
        }
        
        // Zorg ervoor dat de parent juiste styling heeft
        const cartIcon = counter.closest('.cart-icon, .cart-link');
        if (cartIcon) {
          if (cart.item_count === 0) {
            cartIcon.classList.remove('has-items');
          } else {
            cartIcon.classList.add('has-items');
          }
        }
      });
      
      // Update cart drawer als die open is
      const openDrawer = document.querySelector('.cart-notification.active');
      if (openDrawer && typeof CartNotification !== 'undefined' && cart.item_count > 0) {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(cart);
        }
      }
      
      // Stuur event voor andere componenten
      document.dispatchEvent(new CustomEvent('winkelwagen:bijgewerkt', { 
        detail: { cart } 
      }));
      
    } catch (error) {
      console.error('Fout bij ophalen winkelwagen:', error);
    }
  }
  
  /**
   * Open de winkelwagen drawer
   */
  openCartDrawer() {
    // Als gewenst kun je hier de drawer handmatig openen
    const cartDrawerTrigger = document.querySelector('#cart-icon-bubble');
    if (cartDrawerTrigger && cartDrawerTrigger.getAttribute('aria-expanded') === 'false') {
      cartDrawerTrigger.click();
    } else {
      // Alternatieve methode als er een cart-notification component is
      const cartOverlay = document.querySelector('.cart-overlay');
      const cartNotification = document.querySelector('.cart-notification');
      
      if (cartOverlay && cartNotification) {
        cartOverlay.classList.add('active');
        cartNotification.classList.add('active');
      }
    }
  }
  
  /**
   * Afhandelen van klikken op winkelwagen icoon/link
   */
  handleCartTriggerClick(event) {
    // Deze functie kan worden uitgebreid als je specifieke logica nodig hebt
    // wanneer iemand op het winkelwagenicoontje klikt
  }
  
  /**
   * Initialiseer en verwerk bestaande event handlers
   * Dit zorgt ervoor dat we bestaande code niet verstoren
   */
  initProcessExistingHandlers() {
    // Specifiek voor de huidtype-filter sectie
    const huidtypeButtons = document.querySelectorAll('.skincare-section .add-to-cart');
    if (huidtypeButtons.length > 0) {
      console.log('ModerneWinkelwagen: Huidtype knoppen gevonden, integratie toepassen');
      
      // Gebruik onze updateCartDisplay functie na het toevoegen in de bestaande handler
      if (typeof window.updateCartCount !== 'function') {
        window.updateCartCount = () => {
          this.updateCartDisplay();
        };
      }
      
      // Gebruik onze toastnotificatie na succes
      huidtypeButtons.forEach(button => {
        button.addEventListener('click', () => {
          // Wacht op het resultaat van de oorspronkelijke click handler
          setTimeout(() => {
            if (button.classList.contains('success')) {
              this.toonNotificatie('Product toegevoegd aan winkelwagen');
              this.animateCartIcon();
            }
          }, 500);
        });
      });
    }
  }
  
  /**
   * Setup een MutationObserver om dynamisch toegevoegde knoppen te detecteren
   */
  setupMutationObserver() {
    // Creëer een observer configuratie voor toevoegingen aan de DOM
    const config = { 
      childList: true, 
      subtree: true 
    };
    
    // Callback functie om wijzigingen te verwerken
    const mutationCallback = (mutationsList) => {
      let shouldRescan = false;
      
      // Controleer of er relevante elementen zijn toegevoegd
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          // Controleer of er nieuwe knoppen zijn toegevoegd
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const hasCartButtons = node.classList && (
                node.classList.contains('add-to-cart-btn') || 
                node.classList.contains('add-to-cart') ||
                node.classList.contains('product-form__submit') ||
                node.hasAttribute('data-add-to-cart') ||
                (node.getAttribute('name') === 'add' && node.tagName === 'BUTTON')
              );
              
              // Of heeft het element geneste knoppen?
              const hasNestedCartButtons = node.querySelector && node.querySelector(
                '.add-to-cart-btn, [data-add-to-cart], .add-to-cart, .product-form__submit, button[name="add"], .ajax-cart-btn'
              );
              
              if (hasCartButtons || hasNestedCartButtons) {
                shouldRescan = true;
                break;
              }
            }
          }
        }
        
        if (shouldRescan) break;
      }
      
      // Als er nieuwe knoppen zijn gevonden, scan de pagina opnieuw
      if (shouldRescan) {
        console.log('ModerneWinkelwagen: Nieuwe knoppen gedetecteerd, opnieuw scannen');
        this.rescanButtons();
      }
    };
    
    // Creëer en start de observer
    const observer = new MutationObserver(mutationCallback);
    observer.observe(document.body, config);
  }
  
  /**
   * Scan de pagina opnieuw voor knoppen en voeg event listeners toe aan nieuwe knoppen
   */
  rescanButtons() {
    // Haal huidige knoppen op
    const currentButtons = this.cartButtons;
    const currentButtonsArray = Array.from(currentButtons);
    
    // Zoek alle knoppen
    const allButtons = document.querySelectorAll(
      '.add-to-cart-btn, [data-add-to-cart], .add-to-cart, .product-form__submit, button[name="add"], .ajax-cart-btn'
    );
    
    // Vind nieuwe knoppen
    Array.from(allButtons).forEach(button => {
      if (!currentButtonsArray.includes(button)) {
        console.log('ModerneWinkelwagen: Nieuwe knop gevonden, event listener toevoegen', button);
        button.addEventListener('click', this.handleAddToCart.bind(this));
      }
    });
    
    // Update de lijst met knoppen
    this.cartButtons = allButtons;
  }

  createAddToCartButton(variant, productTitle) {
    const addToCartButton = document.createElement('button');
    addToCartButton.classList.add('ajax-cart-add-button', 'gold-button');
    addToCartButton.dataset.cartAdd = variant.id;
    
    if (variant.available) {
      const buttonLabel = document.createElement('span');
      buttonLabel.classList.add('button-label');
      buttonLabel.textContent = this.config.cartStrings?.addToCart || 'Aan winkelwagen toevoegen';
      addToCartButton.appendChild(buttonLabel);
      addToCartButton.disabled = false;
    } else {
      const buttonLabel = document.createElement('span');
      buttonLabel.classList.add('button-label');
      buttonLabel.textContent = this.config.cartStrings?.soldOut || 'Uitverkocht';
      addToCartButton.appendChild(buttonLabel);
      addToCartButton.disabled = true;
    }
    
    return addToCartButton;
  }
}

// Initialiseren wanneer de DOM geladen is
document.addEventListener('DOMContentLoaded', () => {
  // Globaal beschikbaar maken
  window.moderneWinkelwagen = new ModerneWinkelwagen();
  
  // Legacy functienaam voor backward compatibility
  window.updateCartCount = function() {
    window.moderneWinkelwagen.updateCartDisplay();
  };
  
  // Voeg functionaliteit toe om winkelwagen drawer te openen
  window.openCartDrawer = function() {
    window.moderneWinkelwagen.openCartDrawer();
  };
});

// Zorg ervoor dat we een stijlblok toevoegen aan het einde van het document
document.addEventListener('DOMContentLoaded', function() {
  // Aangepaste JS code voor winkelwagen functionaliteit
  console.log('Moderne winkelwagen geïnitialiseerd en CSS geladen');
  
  // Voeg event listeners toe voor animaties
  document.querySelectorAll('.cta-button').forEach(button => {
    button.addEventListener('click', function(event) {
      // Voeg ripple effect toe
      const ripple = document.createElement('span');
      ripple.classList.add('ripple');
      button.appendChild(ripple);
      
      // Verwijder ripple element na animatie
      setTimeout(() => {
        ripple.remove();
      }, 800);
    });
  });
});

// Voeg hulpfuncties toe indien nog niet gedefinieerd
window.routes = window.routes || {
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  cart_add_url: '/cart/add'
};

const fetchConfig = function() {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  };
};

// Debounce functie om snel herhaalde updates te voorkomen
const debounce = function(fn, wait) {
  let timer;
  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    
    this.addEventListener('click', (event) => {
      event.preventDefault();
      
      const cartItems = this.closest('cart-items') || document.querySelector('cart-items');
      cartItems?.updateQuantity(this.dataset.index, 0);
    });
  }
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    // Cache selectoren en elementen
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.createElement('div');
    this.cartStatus = document.getElementById('cart-live-region-text') || document.createElement('div');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
    
    // Eenmalige event delegatie voor de hele cart
    this.setupEventDelegation();
    
    // Laden van cart note (indien aanwezig)
    this.setupCartNote();
  }

  setupEventDelegation() {
    // Gebruik event delegatie voor hoeveelheidswijzigingen - veel minder event listeners!
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[name="updates[]"]')) {
        this.debounce(() => {
          const quantityElement = event.target;
          const index = quantityElement.dataset.index;
          const quantity = parseInt(quantityElement.value);
          
          // Valideer hoeveelheid om onnodige API-calls te voorkomen
          if (quantity >= 0 && quantity !== parseInt(quantityElement.dataset.oldValue || 0)) {
            this.updateQuantity(index, quantity);
            quantityElement.dataset.oldValue = quantity;
          }
        }, 300)();
      }
    });
    
    // Event delegatie voor quantity buttons
    this.addEventListener('click', (event) => {
      if (event.target.matches('.quantity-button')) {
        event.preventDefault();
        
        const button = event.target;
        const quantityInput = button.closest('.quantity-selector').querySelector('input[name="updates[]"]');
        
        if (!quantityInput) return;
        
        const currentValue = parseInt(quantityInput.value);
        const isPlus = button.classList.contains('plus');
        const newValue = isPlus ? currentValue + 1 : Math.max(0, currentValue - 1);
        
        if (newValue !== currentValue) {
          quantityInput.value = newValue;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }
  
  setupCartNote() {
    const cartNoteElement = document.getElementById('cart-note');
    if (!cartNoteElement) return;
    
    // Voorkom onnodige API calls met debounce
    const debouncedNoteUpdate = this.debounce((note) => {
      const body = JSON.stringify({ note });
      
      fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      }).catch(e => console.error('Error bij updaten van cart note:', e));
    }, 500);
    
    cartNoteElement.addEventListener('change', (event) => {
      debouncedNoteUpdate(event.target.value);
    });
  }

  /**
   * Update winkelwagenhoeveelheid - geoptimaliseerd voor minder DOM-bewerkingen
   */
  updateQuantity(line, quantity) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    })
    .then((response) => response.json())
    .then((state) => {
      this.renderContents(state);
      // Probeer de cart count in de header bij te werken
      this.updateHeaderCartCount(state.item_count);
    })
    .catch((error) => {
      this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
      const errors = document.getElementById('cart-errors') || document.querySelector('.cart-errors');
      if (errors) {
        errors.textContent = window.cartStrings.error;
        errors.classList.remove('hidden');
      }
      console.error('Error bij updaten van cart:', error);
    })
    .finally(() => {
      this.disableLoading();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id || 'main-cart-items',
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section--cart-icon-bubble'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section--cart-live-region-text'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id || 'main-cart-footer',
        selector: '.js-contents'
      }
    ];
  }

  renderContents(parsedState) {
    if (!parsedState) return;
    
    // Snelle check of de winkelwagen leeg is - vermijd onnodige DOM-updates
    if (parsedState.item_count === 0 && this.currentItemCount !== 0) {
      this.renderEmptyCart();
      return;
    }
    
    this.currentItemCount = parsedState.item_count;
    
    // DOM updates minimaliseren door alleen de noodzakelijke secties bij te werken
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.querySelector(section.selector);
      const sectionData = parsedState.sections[section.section];
      
      if (elementToReplace && sectionData) {
        elementToReplace.innerHTML = sectionData;
      }
    });
    
    // Gebruik DocumentFragment voor betere rendering prestaties wanneer cart items worden bijgewerkt
    const cartItemsFragment = document.createDocumentFragment();
    const cartItemContainer = document.getElementById('CartItems') || this.querySelector('.cart__items');
    
    if (cartItemContainer) {
      // Alleen renderen als er werkelijk wijzigingen zijn
      const newCartItems = new DOMParser().parseFromString(parsedState.sections['main-cart-items'], 'text/html').querySelector('.cart__items');
      
      if (newCartItems && cartItemContainer.innerHTML !== newCartItems.innerHTML) {
        cartItemContainer.innerHTML = newCartItems.innerHTML;
        
        // InitQuantityInputs terugzetten om quantity buttons te laten werken
        this.initQuantityInputs();
      }
    }
    
    // Cart message bijwerken
    const cartMessage = document.getElementById('cart-message');
    if (cartMessage) {
      if (parsedState.item_count === 0) {
        cartMessage.classList.add('hidden');
      } else {
        cartMessage.classList.remove('hidden');
      }
    }
    
    // Toegankelijkheidsupdate
    this.updateLiveRegions(parsedState);
  }
  
  renderEmptyCart() {
    // Efficiënte manier om lege winkelwagen te tonen
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    
    if (mainCartItems) {
      mainCartItems.innerHTML = `
        <div class="cart--empty-message">
          <h2>${window.cartStrings.empty || 'Je winkelwagen is leeg'}</h2>
          <p>${window.cartStrings.empty_message || 'Voeg producten toe aan je winkelwagen'}</p>
          <a href="/collections/all" class="continue-shopping">${window.cartStrings.continue || 'Verder winkelen'}</a>
        </div>
      `;
    }
    
    if (mainCartFooter) {
      mainCartFooter.classList.add('hidden');
    }
    
    this.currentItemCount = 0;
  }

  updateLiveRegions(parsedState) {
    const cartStatus = document.getElementById('cart-live-region-text');
    if (!cartStatus) return;
    
    cartStatus.textContent = window.cartStrings.update;
    
    if (parsedState.item_count === 0) {
      document.getElementById('main-cart-items')?.setAttribute('aria-hidden', true);
    } else {
      document.getElementById('main-cart-items')?.setAttribute('aria-hidden', false);
    }

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }
  
  updateHeaderCartCount(count) {
    // Update cart counters door de hele site
    document.querySelectorAll('.cart-count, .cart-count-bubble, .icon-cart-count').forEach(counter => {
      if (counter) {
        counter.textContent = count;
        counter.classList.toggle('hidden', count === 0);
      }
    });
  }

  enableLoading(line) {
    // Alleen betreffende item tonen als loading ipv de hele cart
    const cartItems = document.getElementById('CartItems');
    if (!cartItems) return;
    
    const item = cartItems.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.add('is-updating');
    }
  }

  disableLoading() {
    // Verwijder alle loading states
    document.querySelectorAll('.is-updating').forEach(item => {
      item.classList.remove('is-updating');
    });
  }
  
  initQuantityInputs() {
    document.querySelectorAll('.quantity-input').forEach(input => {
      // Voorkom negatieve waarden
      input.addEventListener('input', function() {
        if (this.value < 0) this.value = 0;
      });
    });
  }

  // Utility: debounce voor betere performance
  debounce(fn, wait) {
    let timeout;
    return function() {
      const args = arguments;
      const later = () => {
        timeout = null;
        fn.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Alleen registreren als elementen bestaan
if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

// Snelle start - gebruik requestIdleCallback voor niet-kritische initialisatie
(function() {
  // Voorkom dubbele initialisatie
  if (window.cartInitialized) return;
  window.cartInitialized = true;
  
  const initCart = () => {
    // Check of een cart update nodig is (na product toevoeging)
    const cartUpdateNeeded = new URLSearchParams(window.location.search).get('cart_update') === 'true';
    
    if (cartUpdateNeeded) {
      // Verwijder parameter uit URL zonder pagina reload
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      
      // Update cart counters direct om CLS te voorkomen
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          document.querySelectorAll('.cart-count, .cart-count-bubble, .icon-cart-count').forEach(counter => {
            if (counter) {
              counter.textContent = cart.item_count;
              counter.classList.toggle('hidden', cart.item_count === 0);
            }
          });
        })
        .catch(e => console.error('Error bij controleren cart count:', e));
    }
    
    // Init quantity inputs voor directe interactie
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      cartItems.initQuantityInputs();
    }
  };
  
  // Gebruik requestIdleCallback of setTimeout voor betere mobiele prestaties
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initCart);
  } else {
    setTimeout(initCart, 50);
  }
})();
